/**
 * The chat column and the output window.
 *
 * The conversation lives here, in the browser. The relay is stateless, so what
 * the reader has asked is never persisted server-side — which is both what
 * lets the API scale without a session store and the cheapest possible
 * protection for what is GDPR Article 9 data.
 */

import type { JSX } from 'react'

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Account } from './account'
import { currentAccount, reviewQueue } from './account'
import { AccountControl } from './accountUi'
import { OutputWindow, type Card } from './cards'
import { Composer } from './composer'
import { useConversation } from './conversation'
import { useCorpusFacts } from './corpusFacts'
import { ROOT_ORIGIN } from './origins'
import { ExchangeView, type Exchange } from './exchange'
import { t } from './i18n'
import { GROUNDED, type Mode } from './mode'
import { ActiveVersionsContext } from './activeVersions'
import { useSessionVersions } from './sessionVersions'
import { VersionRow } from './versionPicker'

/**
 * Workspace cards another page may link straight to, by `?view=` name.
 *
 * A deliberate allowlist of plain values rather than anything parsed out of
 * the URL: a card is data, and the set of cards a stranger's link may push
 * onto the stack should be one somebody chose.
 */
const OPENABLE_VIEWS: Record<string, Card> = {
  findings: { kind: 'findings' },
  review: { kind: 'review' },
  themes: { kind: 'themes' },
  threads: { kind: 'threads' },
  packs: { kind: 'packs' },
}


/**
 * Who is signed in, if anyone.
 *
 * Failing quietly is deliberate: a deployment with no account database is not broken, and
 * the header should show nothing rather than an error nobody can act on.
 */
function useSignedInAccount(onLoaded: (account: Account | null) => void): boolean {
  const [known, setKnown] = useState(false)
  useEffect(() => {
    const settle = (loaded: Account | null): void => {
      onLoaded(loaded)
      setKnown(true)
    }
    currentAccount().then(settle, () => settle(null))
    // Once, on mount. `onLoaded` is a setState function and stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return known
}

/**
 * How many findings have citations worth a look.
 *
 * A badge rather than a notice: a standing invitation to tidy up, never an interruption of
 * the reading. The queue only ever holds problems the reader has not already accepted, so a
 * zero here really does mean nothing is outstanding.
 */
function useReviewCount(
  account: Account | null,
  corpus: string | null,
  refreshOn: readonly unknown[],
  onCounted: (total: number) => void,
): void {
  useEffect(() => {
    if (!account || !corpus) return onCounted(0)
    // Both kinds of problem, because the menu is one standing invitation to go
    // and look. Counting only the citation queue would leave a corpus with a
    // broken reference and no citation problems showing nothing at all.
    reviewQueue(corpus).then(
      (queue) =>
        onCounted(
          queue.total +
            queue.dangling.findings.length +
            queue.dangling.threads.length,
        ),
      () => onCounted(0),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, corpus, ...refreshOn])
}

/**
 * A question or a workspace card handed in by another page: `/?q=…`, `/?view=findings`.
 *
 * The study has no URL of its own — it is a card on a stack, not a route — so without the
 * second of these the root site's "Open study" link landed on an empty chat page, which is
 * exactly what it looked like: a promise the app did not keep. Only allowlisted view names
 * are honoured; an unknown one is ignored rather than pushing a broken card.
 */
function useHandedOffRequest(
  onQuestion: (question: string) => void,
  onView: (stack: Card[]) => void,
): void {
  useEffect(() => {
    const parameters = new URLSearchParams(window.location.search)

    const handed = parameters.get('q')
    if (handed) onQuestion(handed)

    const wanted = parameters.get('view')
    const card = wanted ? OPENABLE_VIEWS[wanted] : undefined
    if (card) onView([card])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

/**
 * The wordmark, the way into the text itself, and whoever is signed in.
 *
 * The wordmark links to the root site because that is where the account, the credits and
 * the legal pages live — a corpus site reads the account and never owns it.
 */
function ChatHeader({
  account,
  queued,
  bookIndex,
  onAccountChange,
  onOpen,
}: {
  account: Account | null
  queued: number
  /** Where the pages list every book, as the corpus reports it; null draws no link. */
  bookIndex: string | null
  onAccountChange: (account: Account | null) => void
  onOpen: (card: Card) => void
}): JSX.Element {
  return (
    <header className="chat__header">
      <h1>
        <a className="wordmark" href={ROOT_ORIGIN}>
          {t('app.name')}
        </a>
      </h1>
      <div className="chat__account">
        {/* The generated pages share this origin: every book, readable with no question asked. */}
        {bookIndex !== null && (
          <a className="chat__read" href={bookIndex}>
            {t('app.readTheText')}
          </a>
        )}
        <AccountControl
          account={account}
          queued={queued}
          onChange={onAccountChange}
          onOpen={onOpen}
        />
      </div>
    </header>
  )
}

/**
 * The conversation so far, and the anchor that keeps the newest answer in view.
 *
 * Retry is offered on the last exchange only, and only while nothing is in flight: an
 * earlier answer's history has already been built on by the ones after it, so retrying it
 * would silently rewrite the middle of a conversation the reader can still see.
 */
function Transcript({
  exchanges,
  busy,
  onOpen,
  onRetryLast,
}: {
  exchanges: Exchange[]
  busy: boolean
  onOpen: (card: Card) => void
  onRetryLast: () => void
}): JSX.Element {
  const tail = useNewestInView(exchanges)
  return (
    <div className="chat__scroll">
      {exchanges.length === 0 && (
        <section className="empty">
          <h2>{t('chat.emptyTitle')}</h2>
          <p>{t('chat.emptyBody')}</p>
        </section>
      )}

      {exchanges.map((exchange, index) => (
        <ExchangeView
          key={index}
          exchange={exchange}
          onOpen={onOpen}
          onRetry={index === exchanges.length - 1 && !busy ? onRetryLast : undefined}
        />
      ))}
      <div ref={tail} />
    </div>
  )
}

/**
 * Keep the newest answer in view as it is written.
 *
 * The anchor is an empty element after the last exchange rather than a scroll
 * position computed here: the transcript's height changes with every token, and
 * anything measured would be measuring the answer as it was one frame ago.
 */
function useNewestInView(exchanges: Exchange[]): React.RefObject<HTMLDivElement | null> {
  const tail = useRef<HTMLDivElement>(null)
  useEffect(() => {
    tail.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [exchanges])
  return tail
}

/**
 * The beta banner, above everything and not dismissible.
 *
 * This is the site where a reader's own work is created, so the one notice
 * that has to survive familiarity is the one saying that work is not safe yet.
 * Rendered unconditionally rather than only when signed in, because somebody
 * decides whether to make an account while they are signed out.
 *
 * Its own component for the reason `ChatHeader` and `Composer` are: `App`
 * assembles the shell and says nothing about what any part of it contains.
 */
function BetaWarning(): JSX.Element {
  return (
    <aside className="beta-warning" role="alert">
      <strong>{t('beta.heading')}</strong> {t('beta.body')}
    </aside>
  )
}


/**
 * The output window's stack, and a way to push a card onto it that is stable, so
 * cards can be handed it as a prop.
 */
function useCardStack(): {
  stack: Card[]
  setStack: React.Dispatch<React.SetStateAction<Card[]>>
  openCard: (card: Card) => void
} {
  const [stack, setStack] = useState<Card[]>([])
  const openCard = useCallback((card: Card) => {
    setStack((current) => [...current, card])
  }, [])
  return { stack, setStack, openCard }
}

/**
 * A count bumped when a card changes something the header shows.
 *
 * Opening or closing a card already moves `stack`, but accepting a citation check inside
 * one does not — and a badge still reading "1" over an empty queue is worse than no badge
 * at all. The callback is a prop on the window, never a closure inside a card: the stack
 * has to stay plain data, so what a card needs from here is handed down rather than captured.
 */
function useStudyChanges(): [number, () => void] {
  const [changes, setChanges] = useState(0)
  const changed = useCallback(() => setChanges((count) => count + 1), [])
  return [changes, changed]
}

export default function App(): JSX.Element {
  const [account, setAccount] = useState<Account | null>(null)
  const accountKnown = useSignedInAccount(setAccount)
  const facts = useCorpusFacts() // asked once: every workspace card is scoped to its code
  const session = useSessionVersions(account, accountKnown, facts)
  const { exchanges, busy, askQuestion, retryLast, stop } = useConversation(session.onAnswered)
  const [draft, setDraft] = useState('')
  const { stack, setStack, openCard } = useCardStack()
  // Which discipline the next question asks for. State of the composer rather
  // than of the conversation: the mode rides each ask, and a transcript may
  // legitimately mix the two.
  const [mode, setMode] = useState<Mode>(GROUNDED)
  const [queued, setQueued] = useState(0)
  const [studyChanges, studyChanged] = useStudyChanges()

  useReviewCount(account, facts.code,[stack, studyChanges], setQueued)
  useHandedOffRequest(setDraft, setStack)

  return (
    <ActiveVersionsContext.Provider value={session.forAsk}>
    <div className="layout">
      <BetaWarning />

      <main className="chat">
        <ChatHeader
          account={account}
          queued={queued}
          bookIndex={facts.bookIndex}
          onAccountChange={setAccount}
          onOpen={openCard}
        />

        <Transcript
          exchanges={exchanges}
          busy={busy}
          onOpen={openCard}
          onRetryLast={() => retryLast(mode, session.forAsk)}
        />

        <VersionRow versions={session.versions} added={session.added} onAdd={session.add} onRemove={session.remove} />

        <Composer
          draft={draft}
          busy={busy}
          mode={mode}
          offerResearch={facts.allowsResearch}
          grammar={facts.grammar}
          onDraftChange={setDraft}
          onModeChange={setMode}
          onAsk={(question) => void askQuestion(question, mode, session.forAsk)}
          onStop={stop}
        />
      </main>

      <OutputWindow
        stack={stack}
        account={account}
        corpus={facts.code}
        rootOrigin={ROOT_ORIGIN}
        onAccountChange={setAccount}
        onStudyChanged={studyChanged}
        onBack={() => setStack((current) => current.slice(0, -1))}
        onClose={() => setStack([])}
        onOpen={openCard}
      />
    </div>
    </ActiveVersionsContext.Provider>
  )
}
